import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import ThermalPrintButton from "../../components/print/ThermalPrintButton";
import { buildSrReceipt } from "../../lib/receiptBuilder";
import {
  ArrowLeft, PencilSimple, Warning, CheckCircle, XCircle, Calendar,
  CurrencyInr, Clock, X, WhatsappLogo, ArrowsClockwise, MapPin, Truck,
  Receipt,
} from "@phosphor-icons/react";
import NearbyInvoicesModal from "../../components/NearbyInvoicesModal";

const STATE_STYLE = {
  NEW: "bg-[#F9E4B7] text-[#7A5A11]",
  QUOTED: "bg-[#D4E0D1] text-[#1A3317]",
  APPROVED: "bg-[#C8E6C9] text-[#1B5E20]",
  REJECTED: "bg-[#F4C7B9] text-[#7A2E18]",
  SCHEDULED: "bg-[#BBDEFB] text-[#0D47A1]",
  IN_PROGRESS: "bg-[#F9E4B7] text-[#7A5A11]",
  COMPLETED: "bg-[#2D5A27] text-white",
  CANCELLED: "bg-[#E5E2D9] text-[#5C5A55]",
};

const PRIORITY_STYLE = {
  NORMAL: "bg-[#EFF3EE] text-[#2D5A27] border-[#C8E6C9]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11] border-[#E5DAB7]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18] border-[#F4C7B9]",
  EMERGENCY: "bg-[#F4C7B9] text-[#7A2E18] border-[#F4C7B9]",
  LOW: "bg-[#F7F6F2] text-[#8A8780] border-[#E5E2D9]",
};

export default function ServiceRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  // Iter 238 · duplicate-bill guardrail before jumping to /invoices
  const [nearby, setNearby] = useState(null);

  const goCreateInvoice = () => {
    // The completed SR pre-fills every relevant invoice field.
    const svc = r.service_date || r.completed_at?.slice(0, 10)
                  || r.scheduled_date || r.preferred_date
                  || new Date().toISOString().slice(0, 10);
    setNearby({
      customer_id: r.customer_id,
      service_date: svc,
      prefill: {
        customer_id: r.customer_id,
        service_category_id: r.service_category_id || null,
        package_id: r.package_id || null,
        service_date: svc,
        field_staff_id: r.assigned_staff_id || null,
        source_sr_id: r.id,
      },
    });
  };

  const openInvoiceBuilder = (prefill) => {
    const encoded = btoa(encodeURIComponent(JSON.stringify(prefill)));
    nav(`/finance/invoices?prefill=${encoded}`);
  };

  const load = async () => {
    try {
      const [a, s, v] = await Promise.all([
        api.get(`/service-requests/${id}`),
        api.get("/staff"),
        api.get("/vehicles"),
      ]);
      setR(a.data); setStaff(s.data); setVehicles(v.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const act = async (endpoint, payload = null, msg) => {
    setBusy(true);
    try {
      await api.post(`/service-requests/${id}/${endpoint}`, payload || {});
      toast.success(msg);
      setModal(null); setForm({});
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!r) return <AppShell title="Service Request"><div className="text-sm text-[#8A8780]">Loading…</div></AppShell>;

  const overdue = r.is_overdue_response || r.is_overdue_completion;

  return (
    <AppShell title={r.request_no} subtitle={`${r.customer_name || "—"}${r.customer_code ? " · " + r.customer_code : ""}`}
      actions={
        <div className="flex gap-2">
          <ThermalPrintButton
            label="Print slip"
            variant="outline"
            size="sm"
            dataTestId="sr-thermal-print"
            build={() => buildSrReceipt(r)}
          />
          {(r.status === "NEW" || r.status === "QUOTED") && (
            <Link to={`/operations/service-requests/${id}/edit`} className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
              <PencilSimple size={14}/>Edit
            </Link>
          )}
          <button onClick={() => nav("/operations/service-requests")} className="h-10 px-3 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm">
            <ArrowLeft size={14}/>Back
          </button>
        </div>
      }>
      <div className="grid grid-cols-12 gap-5" data-testid="sr-detail">
        {/* Left: status + actions */}
        <div className="col-span-12 md:col-span-4 space-y-4">
          <div className={`rounded-md p-5 ${overdue ? "border-l-4 border-[#C25D38] bg-white border border-[#E5E2D9]" : "bg-white border border-[#E5E2D9]"}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Status</div>
            <div className={`inline-block mt-2 px-3 py-1 rounded text-sm font-mono ${STATE_STYLE[r.status]}`}>
              {r.status}
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <Row k="Priority" v={
                <span data-testid="sr-priority-pill"
                       className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.NORMAL}`}>
                  {r.priority}
                </span>
              }/>
              <Row k="Payment" v={
                r.paid_upfront || r.payment_status === "paid" ? (
                  <span data-testid="sr-paid-pill"
                         className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#C8E6C9] text-[#1B5E20] border border-[#C8E6C9]">
                    <CheckCircle size={10} weight="fill"/>Paid · Pay & Book
                  </span>
                ) : r.razorpay_order_id ? (
                  <span data-testid="sr-awaiting-payment-pill"
                         className="inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#FEE9C0] text-[#7A5A11] border border-[#F5D688]"
                         title="Customer opened Razorpay Checkout but hasn't completed payment yet">
                    Awaiting payment
                  </span>
                ) : (
                  <span data-testid="sr-unpaid-pill"
                         className="inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#F9E4B7] text-[#7A5A11] border border-[#E5DAB7]">
                    Unpaid
                  </span>
                )
              }/>
              <Row k="Source" v={r.source}/>
              <Row k="Created" v={new Date(r.created_at).toLocaleString()}/>
              {r.responded_at && <Row k="Responded" v={`${r.response_minutes}m after create`}/>}
              {r.completed_at && <Row k="Completed" v={new Date(r.completed_at).toLocaleString()}/>}
              {r.status === "NEW" && (
                <Row k="Response SLA" v={<span className={r.is_overdue_response ? "text-[#C25D38]" : ""}>{new Date(r.response_due_at).toLocaleString()}{r.is_overdue_response ? " ⚠" : ""}</span>}/>
              )}
              {["APPROVED","SCHEDULED","IN_PROGRESS"].includes(r.status) && (
                <Row k="Completion SLA" v={<span className={r.is_overdue_completion ? "text-[#C25D38]" : ""}>{new Date(r.completion_due_at).toLocaleString()}{r.is_overdue_completion ? " ⚠" : ""}</span>}/>
              )}
            </div>
          </div>

          {/* Action buttons per state */}
          <div className="bg-white border border-[#E5E2D9] rounded-md p-5 space-y-2" data-testid="sr-actions">
            {r.status === "NEW" && (
              <>
                <button onClick={() => { setModal("schedule");
                        setForm({scheduled_date: r.preferred_date, scheduled_slot: r.preferred_slot || "",
                                  assigned_staff_id: "", assigned_vehicle_id: "", auto_create_task: true}); }}
                        data-testid="act-assign-direct"
                        className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-sm inline-flex items-center justify-center gap-2">
                  <Truck size={14}/>Assign Staff & Vehicle
                </button>
                <button onClick={() => { setModal("quote"); setForm({quote_amount: "", quote_notes: ""}); }}
                        data-testid="act-quote" className="w-full h-10 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white rounded text-sm inline-flex items-center justify-center gap-2">
                  <CurrencyInr size={14}/>Send quote instead
                </button>
              </>
            )}
            {r.status === "QUOTED" && (
              <>
                <button onClick={() => act("approve", null, "Approved")} disabled={busy} data-testid="act-approve"
                        className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-sm inline-flex items-center justify-center gap-2">
                  <CheckCircle size={14}/>Approve on behalf
                </button>
                <button onClick={() => { setModal("reject"); setForm({reason: ""}); }} data-testid="act-reject"
                        className="w-full h-10 border border-[#C25D38] text-[#C25D38] hover:bg-[#C25D38] hover:text-white rounded text-sm inline-flex items-center justify-center gap-2">
                  <XCircle size={14}/>Reject quote
                </button>
              </>
            )}
            {r.status === "APPROVED" && (
              <button onClick={() => { setModal("schedule");
                      setForm({scheduled_date: r.preferred_date, scheduled_slot: r.preferred_slot || "",
                                assigned_staff_id: "", auto_create_task: true}); }}
                      data-testid="act-schedule"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-sm inline-flex items-center justify-center gap-2">
                <Calendar size={14}/>Schedule
              </button>
            )}
            {r.status === "SCHEDULED" && !r.task_id && (
              <button onClick={() => { setModal("schedule");
                      setForm({scheduled_date: r.scheduled_date, scheduled_slot: r.scheduled_slot || "",
                                assigned_staff_id: r.assigned_staff_id || "",
                                assigned_vehicle_id: r.assigned_vehicle_id || "",
                                auto_create_task: true}); }}
                      data-testid="act-convert"
                      className="w-full h-10 bg-[#2D5A27] text-white rounded text-sm">
                Create Task
              </button>
            )}
            {(r.status === "SCHEDULED" || r.status === "IN_PROGRESS") && (
              <button onClick={() => { setModal("reassign");
                      setForm({assigned_staff_id: r.assigned_staff_id || "",
                                assigned_vehicle_id: r.assigned_vehicle_id || "",
                                reason: ""}); }}
                      data-testid="act-reassign"
                      className="w-full h-10 border border-[#E5E2D9] hover:bg-[#F7F6F2] rounded text-sm inline-flex items-center justify-center gap-2">
                <ArrowsClockwise size={14}/>Reassign
              </button>
            )}
            {(r.status === "SCHEDULED" || r.status === "APPROVED" || r.status === "IN_PROGRESS") && (
              <button onClick={() => { setModal("reschedule");
                      setForm({new_date: r.scheduled_date || "",
                                new_slot: r.scheduled_slot || "",
                                reason: "",
                                notify_customer: true,
                                notify_staff: true}); }}
                      data-testid="act-reschedule"
                      className="w-full h-10 border border-[#E5E2D9] hover:bg-[#F7F6F2] rounded text-sm inline-flex items-center justify-center gap-2">
                <ArrowsClockwise size={14}/>Reschedule
              </button>
            )}
            {r.customer_phone && !["CANCELLED","REJECTED"].includes(r.status) && (
              <div className="grid grid-cols-2 gap-2" data-testid="act-whatsapp-group">
                <button onClick={async () => {
                  try {
                    const res = await api.post(`/service-requests/${id}/whatsapp`, { mode: "api" });
                    toast.success(res.data.sent ? "Sent via WhatsApp API" : "Queued (no phone)");
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                }} data-testid="act-whatsapp-api"
                        className="h-10 bg-[#25D366] hover:bg-[#1EAE54] text-white rounded text-xs inline-flex items-center justify-center gap-1.5">
                  <WhatsappLogo size={13} weight="fill"/>Send via API
                </button>
                <button onClick={async () => {
                  try {
                    const res = await api.post(`/service-requests/${id}/whatsapp`, { mode: "manual" });
                    if (res.data.url) window.open(res.data.url, "_blank");
                    toast.success("WhatsApp opened in new tab");
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                }} data-testid="act-whatsapp-manual"
                        className="h-10 border border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded text-xs inline-flex items-center justify-center gap-1.5">
                  <WhatsappLogo size={13} weight="fill"/>Open WhatsApp
                </button>
                {r.whatsapp_count ? (
                  <div className="col-span-2 text-[10px] text-[#8A8780] text-center">
                    Sent {r.whatsapp_count} time{r.whatsapp_count > 1 ? "s" : ""}
                  </div>
                ) : null}
              </div>
            )}
            {["SCHEDULED","IN_PROGRESS"].includes(r.status) && (
              <button onClick={() => act("complete", null, "Marked completed")} disabled={busy} data-testid="act-complete"
                      className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded text-sm">
                Mark Completed
              </button>
            )}
            {r.status === "COMPLETED" && r.customer_id && (
              <button onClick={goCreateInvoice} data-testid="act-create-invoice"
                      className="w-full h-10 bg-[#B45309] hover:bg-[#92400E] text-white rounded text-sm inline-flex items-center justify-center gap-2">
                <Receipt size={14}/>Create Manual Invoice
              </button>
            )}
            {!["COMPLETED","CANCELLED","REJECTED"].includes(r.status) && (
              <button onClick={() => { setModal("cancel"); setForm({reason: ""}); }} data-testid="act-cancel"
                      className="w-full h-10 border border-[#E5E2D9] text-[#C25D38] hover:bg-[#F7F6F2] rounded text-sm">
                Cancel Request
              </button>
            )}
          </div>
        </div>

        {/* Right: details + events */}
        <div className="col-span-12 md:col-span-8 space-y-4">
          <div className="bg-white border border-[#E5E2D9] rounded-md p-5">
            <div className="font-heading text-lg mb-3">Request details</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Row k="Service" v={r.service_category}/>
              <Row k="Package" v={r.package_name || r.package_id}/>
              <Row k="Preferred" v={r.preferred_date ? `${r.preferred_date}${r.preferred_slot ? " " + r.preferred_slot : ""}` : "—"}/>
              <Row k="Phone" v={r.customer_phone || "—"}/>
              {r.scheduled_date && <Row k="Scheduled" v={`${r.scheduled_date} ${r.scheduled_slot || ""}`}/>}
              {r.assigned_staff_name && <Row k="Assigned" v={r.assigned_staff_name}/>}
              {r.assigned_vehicle_no && <Row k="Vehicle" v={<span className="inline-flex items-center gap-1"><Truck size={10}/>{r.assigned_vehicle_no}</span>}/>}
              {r.vehicle_access != null && <Row k="Vehicle access" v={r.vehicle_access ? "Yes" : "No"}/>}
              {(r.latitude || r.longitude) && (
                <Row k="GPS" v={<a href={r.map_url} target="_blank" rel="noreferrer" className="text-[#2D5A27] underline inline-flex items-center gap-1"><MapPin size={10}/>Open map</a>}/>
              )}
              {r.computed_collection_date && <Row k="System date" v={r.computed_collection_date}/>}
              {r.date_override && <Row k="Override" v={<span className="text-[#C25D38]">⚠ admin</span>}/>}
              {r.last_reassign_reason && <Row k="Last reassign" v={r.last_reassign_reason}/>}
              {r.estimated_weight_kg && <Row k="Est. Weight" v={`${r.estimated_weight_kg} kg`}/>}
              {r.quote_amount != null && <Row k="Quote" v={`₹${r.quote_amount}`}/>}
              {r.task_id && <Row k="Task" v={<Link to="/operations" className="text-[#2D5A27] underline">view task</Link>}/>}
              {r.cancel_reason && <Row k="Cancel reason" v={r.cancel_reason}/>}
            </div>
            <div className="mt-4 pt-4 border-t border-[#E5E2D9]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Address</div>
              <div className="text-sm mt-1">{r.address}</div>
            </div>
            {r.description && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Description</div>
                <div className="text-sm mt-1">{r.description}</div>
              </div>
            )}
            {/* Iter 200 — booking-time chips (priority + service type + paid
                status). The full package/extras breakdown lives in the
                "Total payable" panel below, so this row is now a compact
                strip instead of a full card. */}
            {r.source === "customer_app" && (
              <div className="mt-4 pt-4 border-t border-[#E5E2D9]" data-testid="sr-customer-confirmed">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-2">
                  Customer confirmed at booking
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <span className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${PRIORITY_STYLE[r.priority] || PRIORITY_STYLE.NORMAL}`}>
                    Priority · {r.priority}
                  </span>
                  {r.paid_upfront || r.payment_status === "paid" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#C8E6C9] text-[#1B5E20] border border-[#C8E6C9]"
                           data-testid="sr-confirmed-paid">
                      <CheckCircle size={10} weight="fill"/>Paid · Pay & Book
                    </span>
                  ) : r.razorpay_order_id ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#FEE9C0] text-[#7A5A11] border border-[#F5D688]"
                           data-testid="sr-confirmed-pay-attempted"
                           title="Customer opened Razorpay checkout but hasn't completed payment yet">
                      Awaiting payment
                    </span>
                  ) : r.pay_and_book_intent ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#F9E4B7] text-[#7A5A11] border border-[#E5DAB7]"
                           data-testid="sr-confirmed-intent">
                      Pay & Book · pending
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#F9E4B7] text-[#7A5A11] border border-[#E5DAB7]"
                           data-testid="sr-confirmed-unpaid">
                      Payment pending
                    </span>
                  )}
                  {r.service_category_type && (
                    <span className="inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-white text-[#5C5A55] border border-[#E5E2D9]">
                      {String(r.service_category_type).replace(/_/g, " ")}
                    </span>
                  )}
                  <span className="text-[10px] text-[#8A8780]" data-testid="sr-confirmed-package">
                    {r.package_name || r.package_id || ""}
                  </span>
                </div>
              </div>
            )}
            {/* Iter 64 — attached bag deliveries (Iter 61 clubbing) */}
          </div>

          {/* Iter 200 — Total payable summary (before service scheduling).
              Only rendered when there is an actual package or a non-zero
              payable — a route/admin-created SR with no package would
              otherwise show ₹0.00 next to a real invoice total. */}
          {(r.package_id || (r.payable_total || 0) > 0) && (
          <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="sr-payable">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading text-lg">Total payable</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">
                Before service {r.completed_at ? "· pre-service snapshot" : "schedule"}
              </div>
            </div>
            <div className="rounded-md border border-[#E5E2D9] divide-y divide-[#E5E2D9] text-sm">
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-[#5C5A55]">Package rate
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-[#8A8780]">
                    {r.package_name || "—"}
                  </span>
                </span>
                <span className="font-mono" data-testid="sr-payable-pkg">
                  ₹{Number(r.package_price || 0).toFixed(2)}
                </span>
              </div>
              {(r.extra_charges || []).length > 0 ? (
                (r.extra_charges || []).map((ec) => (
                  <div key={ec.id} className="px-4 py-2 flex items-center justify-between">
                    <span className="text-[#5C5A55]">
                      {ec.label}
                      {ec.priority && (
                        <span className="ml-1 text-[10px] text-[#8A8780] uppercase tracking-wider">
                          · auto ({ec.priority})
                        </span>
                      )}
                    </span>
                    <span className="font-mono text-[#7A2E18]">+₹{Number(ec.amount).toFixed(2)}</span>
                  </div>
                ))
              ) : (
                <div className="px-4 py-2 text-[11px] text-[#8A8780] italic">No extra charges</div>
              )}
              <div className="px-4 py-2 flex items-center justify-between bg-[#F7F6F2]">
                <span className="font-semibold">Grand total payable</span>
                <span className="font-mono font-semibold" data-testid="sr-payable-total">
                  ₹{Number(r.payable_total ?? ((r.package_price || 0) + (r.extra_charges_total || 0))).toFixed(2)}
                </span>
              </div>
              <div className="px-4 py-2 flex items-center justify-between">
                <span className="text-[#5C5A55]">Payment status</span>
                {r.paid_upfront || r.payment_status === "paid" ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#C8E6C9] text-[#1B5E20] border border-[#C8E6C9]"
                         data-testid="sr-payable-paid">
                    <CheckCircle size={10} weight="fill"/>Paid
                    {r.payment_source === "pay_and_book_razorpay" ? " · Razorpay" : ""}
                  </span>
                ) : r.razorpay_order_id ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#FEE9C0] text-[#7A5A11] border border-[#F5D688]"
                         data-testid="sr-payable-awaiting">
                    Awaiting payment · Razorpay order created
                  </span>
                ) : (
                  <span className="inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#F9E4B7] text-[#7A5A11] border border-[#E5DAB7]"
                         data-testid="sr-payable-unpaid">
                    Payment pending
                  </span>
                )}
              </div>
              {r.paid_at && (
                <div className="px-4 py-2 flex items-center justify-between text-[11px] text-[#8A8780]">
                  <span>Paid at</span>
                  <span className="font-mono">{new Date(r.paid_at).toLocaleString()}</span>
                </div>
              )}
              {r.razorpay_payment_id && (
                <div className="px-4 py-2 flex items-center justify-between text-[11px] text-[#8A8780]">
                  <span>Razorpay ref</span>
                  <span className="font-mono">{r.razorpay_payment_id}</span>
                </div>
              )}
            </div>
            {r.completed_at && (
              <div className="mt-2 text-[10px] text-[#5C5A55]">
                Note — the excess-quantity waste collected on-site is billed separately on the invoice below.
              </div>
            )}
          </div>
          )}

          {/* Iter 200 — Assignment & Job execution snapshot */}
          {(r.assigned_staff_name || r.task || r.scheduled_date) && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="sr-job-execution">
              <div className="font-heading text-lg mb-3">Assignment & job execution</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {r.assigned_staff_name && (
                  <Row k="Assigned staff" v={
                    <span data-testid="sr-job-staff" className="inline-flex items-center gap-1">
                      <Truck size={11}/>{r.assigned_staff_name}
                    </span>
                  }/>
                )}
                {r.assigned_vehicle_no && (
                  <Row k="Vehicle" v={r.assigned_vehicle_no}/>
                )}
                {r.scheduled_date && (
                  <Row k="Scheduled" v={`${r.scheduled_date}${r.scheduled_slot ? " · " + r.scheduled_slot : ""}`}/>
                )}
                {r.task?.sequence != null && (
                  <Row k="Route order" v={`#${r.task.sequence}`}/>
                )}
                {r.task?.status && (
                  <Row k="Task status" v={
                    <span data-testid="sr-job-task-status"
                           className={`inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                             r.task.status === "completed" ? "bg-[#2D5A27] text-white"
                               : r.task.status === "in_progress" ? "bg-[#F9E4B7] text-[#7A5A11]"
                               : "bg-[#E5E2D9] text-[#5C5A55]"
                           }`}>
                      {String(r.task.status).replace(/_/g, " ")}
                    </span>
                  }/>
                )}
                {r.task?.started_at && (
                  <Row k="Job started" v={
                    <span data-testid="sr-job-started">
                      {new Date(r.task.started_at).toLocaleString()}
                      {r.task.started_by_staff_name && (
                        <span className="text-[10px] text-[#8A8780] ml-1">
                          by {r.task.started_by_staff_name}
                        </span>
                      )}
                    </span>
                  }/>
                )}
                {r.task?.arrival_time && (
                  <Row k="Arrived at site" v={new Date(r.task.arrival_time).toLocaleString()}/>
                )}
                {r.task?.completed_at && (
                  <Row k="Job completed" v={
                    <span data-testid="sr-job-completed">
                      {new Date(r.task.completed_at).toLocaleString()}
                    </span>
                  }/>
                )}
              </div>
            </div>
          )}

          {/* Iter 200 — After-service invoice snapshot */}
          {r.invoice && (
            <div className="bg-white border border-[#E5E2D9] rounded-md p-5" data-testid="sr-invoice-panel">
              <div className="flex items-center justify-between mb-3">
                <div className="font-heading text-lg">After-service invoice</div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                  r.invoice.status === "issued" || r.invoice.status === "paid"
                    ? "bg-[#C8E6C9] text-[#1B5E20]"
                    : "bg-[#F9E4B7] text-[#7A5A11]"
                }`} data-testid="sr-invoice-status">
                  {r.invoice.status || "draft"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <Row k="Invoice #" v={
                  <span className="font-mono" data-testid="sr-invoice-no">{r.invoice.invoice_no || "—"}</span>
                }/>
                <Row k="Payment" v={
                  r.invoice.payment_status === "paid" ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#C8E6C9] text-[#1B5E20]"
                           data-testid="sr-invoice-paid">
                      <CheckCircle size={10} weight="fill"/>Paid
                    </span>
                  ) : (
                    <span className="inline-flex px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-[#F9E4B7] text-[#7A5A11]"
                           data-testid="sr-invoice-unpaid">
                      Unpaid
                    </span>
                  )
                }/>
                {r.invoice.subtotal != null && (
                  <Row k="Subtotal" v={<span className="font-mono">₹{Number(r.invoice.subtotal).toFixed(2)}</span>}/>
                )}
                {r.invoice.excess_total != null && r.invoice.excess_total > 0 && (
                  <Row k="Excess-quantity" v={
                    <span className="font-mono text-[#7A2E18]" data-testid="sr-invoice-excess">
                      +₹{Number(r.invoice.excess_total).toFixed(2)}
                      {r.invoice.excess_kg != null && (
                        <span className="ml-1 text-[10px] text-[#8A8780]">
                          ({Number(r.invoice.excess_kg).toFixed(2)} kg over allowance)
                        </span>
                      )}
                    </span>
                  }/>
                )}
                <Row k="Grand total" v={
                  <span className="font-mono font-semibold" data-testid="sr-invoice-total">
                    ₹{Number(r.invoice.total || 0).toFixed(2)}
                  </span>
                }/>
                {(r.invoice.issued_at || r.invoice.issued_date || r.invoice.issued_on) && (
                  <Row k="Issued" v={new Date(r.invoice.issued_at || r.invoice.issued_date || r.invoice.issued_on).toLocaleString()}/>
                )}
                {r.invoice.paid_at && (
                  <Row k="Paid at" v={new Date(r.invoice.paid_at).toLocaleString()}/>
                )}
                {r.invoice.prepaid && (
                  <Row k="Source" v={<span className="text-[10px] uppercase tracking-wider text-[#1B5E20]">Prepaid at booking</span>}/>
                )}
              </div>
              {r.invoice.excess_lines && r.invoice.excess_lines.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#E5E2D9]">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mb-1">Excess-quantity breakdown</div>
                  <div className="space-y-1 text-xs" data-testid="sr-invoice-excess-lines">
                    {r.invoice.excess_lines.map((el, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span>{el.category || el.name} · {el.excess_kg ?? el.kg} kg × ₹{el.rate}</span>
                        <span className="font-mono text-[#7A2E18]">+₹{Number(el.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <Link to={`/finance/invoices?invoice=${r.invoice.id}`}
                       data-testid="sr-invoice-open"
                       className="h-9 px-3 border border-[#2D5A27] text-[#2D5A27] hover:bg-[#2D5A27] hover:text-white rounded text-xs inline-flex items-center gap-1.5">
                  Open in Finance
                </Link>
                {r.invoice.pdf_url && (
                  <a href={r.invoice.pdf_url} target="_blank" rel="noreferrer"
                      data-testid="sr-invoice-pdf"
                      className="h-9 px-3 border border-[#E5E2D9] rounded text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
                    Download PDF
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex items-center gap-1">
              <Clock size={11}/>Activity timeline
            </div>
            <div className="divide-y divide-[#E5E2D9]">
              {(r.events || []).slice().reverse().map((ev, i) => (
                <div key={i} className="px-5 py-3 flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A27] mt-2 shrink-0"/>
                  <div className="flex-1">
                    <div className="text-xs font-mono text-[#2D5A27]">{ev.type}</div>
                    <div className="text-[11px] text-[#8A8780]">
                      by {ev.actor_name || "system"} · {new Date(ev.ts).toLocaleString()}
                    </div>
                    {ev.reason && <div className="text-xs text-[#C25D38] mt-1">Reason: {ev.reason}</div>}
                    {ev.amount != null && <div className="text-xs text-[#5C5A55] mt-1">₹{ev.amount}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <Modal title={MODAL_TITLES[modal]} onClose={() => setModal(null)}>
          {modal === "quote" && (
            <>
              <L>Quote amount (₹)</L>
              <input type="number" step="0.01" value={form.quote_amount}
                     onChange={e => setForm(f => ({...f, quote_amount: e.target.value}))}
                     data-testid="quote-amount" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              <L>Notes (optional)</L>
              <textarea rows={2} value={form.quote_notes}
                        onChange={e => setForm(f => ({...f, quote_notes: e.target.value}))}
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              <button onClick={() => act("quote", {quote_amount: Number(form.quote_amount), quote_notes: form.quote_notes || null}, "Quote sent")} disabled={busy}
                      data-testid="quote-save" className="mt-4 w-full h-10 bg-[#2D5A27] text-white rounded text-sm">
                Send quote
              </button>
            </>
          )}
          {modal === "schedule" && (
            <>
              <L>Scheduled Date</L>
              <input type="date" value={form.scheduled_date}
                     onChange={e => setForm(f => ({...f, scheduled_date: e.target.value}))}
                     data-testid="sched-date" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              <L>Slot</L>
              <input placeholder="09:00-12:00" value={form.scheduled_slot}
                     onChange={e => setForm(f => ({...f, scheduled_slot: e.target.value}))}
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              <L>Assign staff (optional)</L>
              <select value={form.assigned_staff_id}
                      onChange={e => setForm(f => ({...f, assigned_staff_id: e.target.value}))}
                      data-testid="sched-staff" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                <option value="">— unassigned —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.staff_code})</option>)}
              </select>
              <L>Assign vehicle (optional)</L>
              <select value={form.assigned_vehicle_id || ""}
                      onChange={e => setForm(f => ({...f, assigned_vehicle_id: e.target.value}))}
                      data-testid="sched-vehicle" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                <option value="">— no vehicle —</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_no}{v.type ? ` (${v.type})` : ""}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm mt-3 cursor-pointer" data-testid="sched-auto">
                <input type="checkbox" checked={!!form.auto_create_task}
                       onChange={e => setForm(f => ({...f, auto_create_task: e.target.checked}))}/>
                <span>Auto-create task in Operations</span>
              </label>
              <button onClick={() => act("schedule", {
                scheduled_date: form.scheduled_date,
                scheduled_slot: form.scheduled_slot || null,
                assigned_staff_id: form.assigned_staff_id || null,
                assigned_vehicle_id: form.assigned_vehicle_id || null,
                auto_create_task: !!form.auto_create_task,
              }, "Scheduled")} disabled={busy} data-testid="sched-save"
                      className="mt-4 w-full h-10 bg-[#2D5A27] text-white rounded text-sm">
                Schedule
              </button>
            </>
          )}
          {modal === "reassign" && (
            <>
              <L>Staff</L>
              <select value={form.assigned_staff_id}
                      onChange={e => setForm(f => ({...f, assigned_staff_id: e.target.value}))}
                      data-testid="reassign-staff" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                <option value="">— unassigned —</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.staff_code})</option>)}
              </select>
              <L>Vehicle</L>
              <select value={form.assigned_vehicle_id}
                      onChange={e => setForm(f => ({...f, assigned_vehicle_id: e.target.value}))}
                      data-testid="reassign-vehicle" className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
                <option value="">— no vehicle —</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_no}</option>)}
              </select>
              <L>Reason * (minimum 3 chars)</L>
              <textarea rows={3} value={form.reason}
                        onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                        data-testid="reassign-reason"
                        placeholder="e.g., original staff on leave"
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              <button onClick={() => act("reassign", {
                assigned_staff_id: form.assigned_staff_id || null,
                assigned_vehicle_id: form.assigned_vehicle_id || null,
                reason: form.reason,
              }, "Reassigned")} disabled={busy || (form.reason || "").trim().length < 3}
                      data-testid="reassign-save"
                      className="mt-4 w-full h-10 bg-[#2D5A27] disabled:opacity-60 text-white rounded text-sm">
                Save reassignment
              </button>
            </>
          )}
          {modal === "reschedule" && (
            <>
              <L>New date *</L>
              <input type="date" value={form.new_date}
                     onChange={e => setForm(f => ({...f, new_date: e.target.value}))}
                     data-testid="reschedule-date"
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white"/>
              <L>New slot (optional)</L>
              <input value={form.new_slot}
                     onChange={e => setForm(f => ({...f, new_slot: e.target.value}))}
                     data-testid="reschedule-slot"
                     placeholder="e.g. 09:00-11:00"
                     className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white"/>
              <L>Reason * (minimum 3 chars)</L>
              <textarea rows={3} value={form.reason}
                        onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                        data-testid="reschedule-reason"
                        placeholder="e.g., customer requested a later date"
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              <div className="mt-3 space-y-1.5">
                <label className="flex items-center gap-2 text-xs text-[#5C5A55]">
                  <input type="checkbox" checked={!!form.notify_customer}
                         data-testid="reschedule-notify-customer"
                         onChange={e => setForm(f => ({...f, notify_customer: e.target.checked}))}/>
                  Notify customer (in-app + email)
                </label>
                <label className="flex items-center gap-2 text-xs text-[#5C5A55]">
                  <input type="checkbox" checked={!!form.notify_staff}
                         data-testid="reschedule-notify-staff"
                         onChange={e => setForm(f => ({...f, notify_staff: e.target.checked}))}/>
                  Notify assigned staff
                </label>
              </div>
              <button onClick={() => act("reschedule", {
                new_date: form.new_date,
                new_slot: form.new_slot || null,
                reason: form.reason,
                notify_customer: !!form.notify_customer,
                notify_staff: !!form.notify_staff,
              }, "Rescheduled")} disabled={busy || !form.new_date || (form.reason || "").trim().length < 3}
                      data-testid="reschedule-save"
                      className="mt-4 w-full h-10 bg-[#2D5A27] disabled:opacity-60 text-white rounded text-sm">
                Save reschedule
              </button>
            </>
          )}
          {(modal === "cancel" || modal === "reject") && (
            <>
              <L>Reason</L>
              <textarea rows={3} value={form.reason}
                        onChange={e => setForm(f => ({...f, reason: e.target.value}))}
                        data-testid={`${modal}-reason`}
                        className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
              <button onClick={() => act(modal, {reason: form.reason}, modal === "cancel" ? "Cancelled" : "Rejected")}
                      disabled={busy || !form.reason.trim()}
                      data-testid={`${modal}-confirm`}
                      className="mt-4 w-full h-10 bg-[#C25D38] disabled:opacity-60 text-white rounded text-sm">
                Confirm {modal}
              </button>
            </>
          )}
        </Modal>
      )}

      {/* Iter 238 · Duplicate-bill guardrail before jumping to /invoices */}
      {nearby && (
        <NearbyInvoicesModal
          customerId={nearby.customer_id}
          serviceDate={nearby.service_date}
          windowDays={2}
          onCancel={() => setNearby(null)}
          onProceed={() => { openInvoiceBuilder(nearby.prefill); setNearby(null); }}
        />
      )}
    </AppShell>
  );
}

const MODAL_TITLES = {
  quote: "Send quote", schedule: "Schedule request",
  cancel: "Cancel request", reject: "Reject quote",
  reassign: "Reassign staff / vehicle",
};

const Row = ({ k, v }) => (
  <div className="flex justify-between">
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#8A8780]">{k}</span>
    <span className="font-mono text-xs text-[#2D2B26] text-right max-w-[60%] truncate">{v}</span>
  </div>
);
const L = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mt-3 mb-2">{children}</div>
);
const Modal = ({ title, children, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-md w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-heading text-lg">{title}</div>
        <button onClick={onClose}><X size={16}/></button>
      </div>
      {children}
    </div>
  </div>
);
